import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import { signOut, useSession } from '../auth.js';
import { PremiumDialog } from '../components/PremiumDialog.js';
import { PremiumDialogProvider } from '../context/PremiumDialogContext.js';
import {
  clearWonderPushUserId,
  revokePushConsent,
  setWonderPushUserId,
} from '../push/wonderpush.js';

// ── Avatar menu ────────────────────────────────────────────────────────────────

interface AvatarMenuProps {
  email: string;
  onSignOut: () => void;
}

function AvatarMenu({ email, onSignOut }: AvatarMenuProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const initial = email[0]?.toUpperCase() ?? '?';

  // Outside-click → close (user clicked elsewhere, no focus return).
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (
        buttonRef.current?.contains(e.target as Node) ||
        menuRef.current?.contains(e.target as Node)
      )
        return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // Esc → close + return focus to the trigger button.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // When menu opens, move focus to the first menu item.
  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
  }, [open]);

  function getItems(): HTMLElement[] {
    return Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
  }

  function onMenuKeyDown(e: React.KeyboardEvent) {
    const items = getItems();
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLElement);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === 'Tab') {
      // Keep focus inside the menu — cycle forward/backward without leaving.
      e.preventDefault();
      if (e.shiftKey) {
        items[(idx - 1 + items.length) % items.length]?.focus();
      } else {
        items[(idx + 1) % items.length]?.focus();
      }
    }
  }

  return (
    <div className="relative ml-auto shrink-0">
      <button
        ref={buttonRef}
        type="button"
        id="avatar-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="avatar-menu"
        aria-label={`User menu for ${email}`}
        onClick={() => setOpen((o) => !o)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-leaf text-xs font-semibold text-white
                   hover:bg-leaf-deep focus:outline-none focus:ring-2 focus:ring-leaf focus:ring-offset-2
                   dark:ring-offset-shell"
      >
        {initial}
      </button>

      {open && (
        <div
          ref={menuRef}
          id="avatar-menu"
          role="menu"
          aria-labelledby="avatar-btn"
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 top-full z-50 mt-1 min-w-[180px] rounded-lg border border-husk
                     bg-white py-1 shadow-md dark:border-ink/20 dark:bg-shell"
        >
          {/* Email — label, not interactive */}
          <p
            className="border-b border-husk px-3 py-2 text-xs text-ink-soft dark:border-ink/20"
            aria-hidden
          >
            {email}
          </p>

          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-sm text-ink hover:bg-husk/50
                       focus:bg-husk/50 focus:outline-none dark:hover:bg-ink/10 dark:focus:bg-ink/10"
          >
            Settings
          </Link>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="block w-full px-3 py-2 text-left text-sm text-ink hover:bg-husk/50
                       focus:bg-husk/50 focus:outline-none dark:hover:bg-ink/10 dark:focus:bg-ink/10"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ── Shared NavLink class helper ────────────────────────────────────────────────

function navLinkClass({ isActive }: { isActive: boolean }) {
  return `shrink-0 text-sm ${
    isActive ? 'text-ink dark:text-ink' : 'text-ink-soft hover:text-ink dark:hover:text-ink'
  }`;
}

// ── Layout ─────────────────────────────────────────────────────────────────────

export default function RootLayout() {
  const navigate = useNavigate();
  const { data: session } = useSession();

  // Bind the WonderPush subscription to our userId after auth (ADR-0019 §2).
  const userId = session?.user.id;
  useEffect(() => {
    if (userId) void setWonderPushUserId(userId);
  }, [userId]);

  async function onSignOut() {
    await revokePushConsent();
    clearWonderPushUserId();
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    <PremiumDialogProvider>
      <PremiumDialog />
      <div className="flex min-h-dvh flex-col">
        <header className="border-b border-husk dark:border-husk">
          <nav
            className="mx-auto flex max-w-2xl items-center gap-4 px-8 py-4"
            aria-label="Main navigation"
          >
            <Link
              to="/"
              className="flex shrink-0 items-center gap-2 text-base font-medium tracking-tight"
            >
              <img src="/nutians-mark.png" alt="" width={24} height={24} className="h-6 w-6" />
              Nutians
            </Link>

            <NavLink to="/coaching" className={navLinkClass}>
              Coaching
            </NavLink>
            <NavLink to="/planning" className={navLinkClass}>
              Planning
            </NavLink>
            <NavLink to="/measurements" className={navLinkClass}>
              Measurements
            </NavLink>
            <NavLink to="/sharing" className={navLinkClass}>
              Sharing
            </NavLink>
            <NavLink to="/chat" className={navLinkClass}>
              Chat
            </NavLink>

            {/* Avatar menu — contains Settings + Sign out + email label */}
            {session?.user.email ? (
              <AvatarMenu email={session.user.email} onSignOut={onSignOut} />
            ) : (
              /* Fallback when session not yet loaded — preserve layout */
              <div className="ml-auto h-8 w-8" />
            )}
          </nav>
        </header>

        <main className="mx-auto flex w-full max-w-2xl min-h-0 flex-1 flex-col px-8 py-8">
          <Outlet />
        </main>
      </div>
    </PremiumDialogProvider>
  );
}
